import type { AiOutputReportKind } from '../domain/ai-output-report/AiOutputReportKind';
import type { AiOutputReportStatus } from '../domain/ai-output-report/AiOutputReportStatus';
import type { ReportFieldKey } from '../domain/playbook/ReportFieldKey';
import { Language } from '../domain/user/Language';
import { LanguageParser } from '../domain/user/LanguageParser';
import type { WorkOrderPriority } from '../domain/work-order/WorkOrderPriority';
import type { WorkOrderStatus } from '../domain/work-order/WorkOrderStatus';
import type { WorkSheetStatus } from '../domain/work-sheet/WorkSheetStatus';
import type { WorkSheetStepNonCompletionReason } from '../domain/work-sheet/WorkSheetStepNonCompletionReason';
import type { WorkSheetStepStatus } from '../domain/work-sheet/WorkSheetStepStatus';
import enDomainMessages from './en/domain.json' with { type: 'json' };
import esDomainMessages from './es/domain.json' with { type: 'json' };

export type YesNoKey = `${boolean}`;

export type DomainMessageCatalog = Readonly<{
	workOrderStatus: Readonly<Record<WorkOrderStatus, string>>;
	workOrderPriority: Readonly<Record<WorkOrderPriority, string>>;
	workSheetStatus: Readonly<Record<WorkSheetStatus, string>>;
	workSheetStepStatus: Readonly<Record<WorkSheetStepStatus, string>>;
	workSheetStepNonCompletionReason: Readonly<Record<WorkSheetStepNonCompletionReason, string>>;
	yesNo: Readonly<Record<YesNoKey, string>>;
	reportField: Readonly<Record<ReportFieldKey, string>>;
	aiOutputReportStatus: Readonly<Record<AiOutputReportStatus, string>>;
	aiOutputReportKind: Readonly<Record<AiOutputReportKind, string>>;
}>;

export type DomainMessageFamily = keyof DomainMessageCatalog;

export type DomainMessageKey<Family extends DomainMessageFamily> = keyof DomainMessageCatalog[Family] & string;

const DOMAIN_MESSAGES = {
	[Language.ES]: esDomainMessages,
	[Language.EN]: enDomainMessages,
} satisfies Readonly<Record<Language, DomainMessageCatalog>>;

export class DomainMessages {
	private constructor() {}

	static readonly NAMESPACE = 'domain';

	static forLanguage(language: Language): DomainMessageCatalog {
		const supportedLanguage = Object.keys(DOMAIN_MESSAGES).includes(language) ? language : LanguageParser.DEFAULT_LANGUAGE;
		return DOMAIN_MESSAGES[supportedLanguage];
	}

	static forLocale(locale: string): DomainMessageCatalog {
		return DomainMessages.forLanguage(LanguageParser.fromLocale(locale));
	}

	static label<Family extends DomainMessageFamily>(family: Family, key: DomainMessageKey<Family>, language: Language): string {
		const familyMessages: Readonly<Record<string, string>> = DomainMessages.forLanguage(language)[family];
		return Object.keys(familyMessages).includes(key) ? familyMessages[key] : key;
	}

	static yesNo(answer: boolean, language: Language): string {
		const answerKey: YesNoKey = answer ? 'true' : 'false';
		return DomainMessages.label('yesNo', answerKey, language);
	}
}
